import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export const TEMPLATE_CATEGORIES = [
  'Gates',
  'Custom Hardware',
  'Fabric & Mesh',
  'Posts & Rails',
  'Concrete & Materials',
  'Labor & Services',
  'Misc / Other',
];

// Same product-category list used by the price list and NewDocument.
export const PRODUCT_CATEGORIES = [
  'Chain Link',
  'Galv Fittings',
  'Black Fittings',
  'Galv Pipe',
  'Black Pipe',
  'Residential Swing Gates Galv',
  'Commercial Swing Gates Galv',
  'Residential Gates Black',
  'Commercial Gates Black',
  'Wood',
  'Wood Hardware',
  'Split Rail',
  'Tools/Misc',
  'PVC',
  'Cantilever Gate',
  'Aluminum Slide Gate Hardware',
  'Aluminum',
];

// Old single-item templates (created before the bundle upgrade) had fields
// `description`, `unit`, `defaultPrice`, `defaultCost` directly on the doc.
// Normalize them into one-item bundles on read — no Firestore migration needed.
export const normalizeTemplate = (t) => {
  if (Array.isArray(t.items) && t.items.length > 0) return t;
  return {
    ...t,
    description: '',
    items: [{
      id: 'item-1',
      description: t.description || t.name || '',
      unit: t.unit || 'pc',
      defaultQty: 1,
      defaultPrice: parseFloat(t.defaultPrice) || 0,
      defaultCost: parseFloat(t.defaultCost) || 0,
    }],
  };
};

const customBundleTotal = (template) => {
  const items = template.items || [];
  const price = items.reduce((s, i) => s + (parseFloat(i.defaultQty || 0) * parseFloat(i.defaultPrice || 0)), 0);
  return { count: items.length, price };
};

function TemplatesPickerModal({ open, onClose, onSelectCustomTemplate, onSelectStockBundle }) {
  const navigate = useNavigate();
  const [pickerTab, setPickerTab] = useState('custom');
  const [customTemplates, setCustomTemplates] = useState([]);
  const [stockBundles, setStockBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [loadedOnce, setLoadedOnce] = useState(false);

  useEffect(() => {
    if (open && !loadedOnce) {
      loadAll();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset category filter when switching tabs (different category sets)
  useEffect(() => {
    setCategory('All');
  }, [pickerTab]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [customSnap, stockSnap] = await Promise.all([
        getDocs(collection(db, 'specialOrderTemplates')),
        getDocs(collection(db, 'stockBundles')),
      ]);
      const custom = customSnap.docs.map(d => normalizeTemplate({ id: d.id, ...d.data() }));
      custom.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const stock = stockSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      stock.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCustomTemplates(custom);
      setStockBundles(stock);
      setLoadedOnce(true);
    } catch (err) {
      console.error('Error loading templates/bundles:', err);
    }
    setLoading(false);
  };

  const filterFor = (list) => list.filter(t => {
    const term = search.trim().toLowerCase();
    const matchSearch = !term ||
      t.name?.toLowerCase().includes(term) ||
      t.description?.toLowerCase().includes(term) ||
      (t.items || []).some(i => i.description?.toLowerCase().includes(term) || i.sku?.toLowerCase().includes(term));
    const matchCategory = category === 'All' || t.category === category;
    return matchSearch && matchCategory;
  });

  const filteredCustom = filterFor(customTemplates);
  const filteredStock = filterFor(stockBundles);
  const activeCategoryList = pickerTab === 'custom' ? TEMPLATE_CATEGORIES : PRODUCT_CATEGORIES;
  const activeList = pickerTab === 'custom' ? customTemplates : stockBundles;
  const filteredActive = pickerTab === 'custom' ? filteredCustom : filteredStock;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(3,4,94,0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', boxShadow: '0 8px 24px rgba(3,4,94,0.15)', fontFamily: 'Manrope, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E3ECF5' }}>
          <h2 className="text-xl font-bold" style={{ color: '#03045E' }}>Add from Templates</h2>
          <button onClick={onClose} className="text-2xl leading-none" style={{ color: '#6B7C93' }}
            onMouseEnter={e => e.currentTarget.style.color = '#A32D2D'} onMouseLeave={e => e.currentTarget.style.color = '#6B7C93'}>✕</button>
        </div>

        {/* Tabs */}
        <div className="flex" style={{ borderBottom: '1px solid #E3ECF5' }}>
          <button
            onClick={() => setPickerTab('custom')}
            className="px-6 py-3 font-bold border-b-2 transition"
            style={pickerTab === 'custom'
              ? { borderColor: '#0077B6', color: '#0077B6' }
              : { borderColor: 'transparent', color: '#6B7C93' }}
          >
            📋 Custom Templates ({customTemplates.length})
          </button>
          <button
            onClick={() => setPickerTab('stock')}
            className="px-6 py-3 font-bold border-b-2 transition"
            style={pickerTab === 'stock'
              ? { borderColor: '#0077B6', color: '#0077B6' }
              : { borderColor: 'transparent', color: '#6B7C93' }}
          >
            📦 Stock Bundles ({stockBundles.length})
          </button>
        </div>

        {/* Search + filter */}
        <div className="p-4 flex flex-wrap gap-3" style={{ borderBottom: '1px solid #E3ECF5' }}>
          <input
            type="text"
            placeholder={pickerTab === 'custom' ? 'Search custom templates...' : 'Search stock bundles...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] rounded-lg px-4 py-2 focus:outline-none"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
            onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
            autoFocus
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg px-3 py-2 focus:outline-none"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
            onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
          >
            <option value="All">All Categories</option>
            {activeCategoryList.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-center py-12" style={{ color: '#6B7C93' }}>Loading…</p>
          ) : activeList.length === 0 ? (
            <div className="text-center py-12">
              <p className="mb-4" style={{ color: '#6B7C93' }}>
                {pickerTab === 'custom' ? 'No custom templates yet.' : 'No stock bundles yet.'}
              </p>
              <button
                onClick={() => { onClose(); navigate('/templates'); }}
                className="font-bold hover:underline"
                style={{ color: '#0077B6' }}
              >
                + Create your first {pickerTab === 'custom' ? 'template' : 'bundle'}
              </button>
            </div>
          ) : filteredActive.length === 0 ? (
            <p className="text-center py-12" style={{ color: '#6B7C93' }}>No {pickerTab === 'custom' ? 'templates' : 'bundles'} match.</p>
          ) : pickerTab === 'custom' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredCustom.map(t => {
                const { count, price } = customBundleTotal(t);
                return (
                  <div
                    key={t.id}
                    onClick={() => onSelectCustomTemplate(t)}
                    className="rounded-lg p-4 cursor-pointer transition"
                    style={{ backgroundColor: '#CDE7F5', border: '1px solid #E3ECF5' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E6F1FB'; e.currentTarget.style.borderColor = '#0077B6'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#CDE7F5'; e.currentTarget.style.borderColor = '#E3ECF5'; }}
                  >
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <p className="font-bold text-sm" style={{ color: '#03045E' }}>{t.name}</p>
                      {t.category && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold flex-shrink-0" style={{ backgroundColor: '#E6F1FB', color: '#0077B6' }}>{t.category}</span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-xs mb-2 line-clamp-2" style={{ color: '#6B7C93' }}>{t.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid #E3ECF5' }}>
                      <span className="text-xs" style={{ color: '#6B7C93' }}>📦 {count} item{count !== 1 ? 's' : ''}</span>
                      <span className="font-bold text-sm" style={{ color: '#0077B6' }}>${price.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredStock.map(b => {
                const count = (b.items || []).length;
                return (
                  <div
                    key={b.id}
                    onClick={() => onSelectStockBundle(b)}
                    className="rounded-lg p-4 cursor-pointer transition"
                    style={{ backgroundColor: '#CDE7F5', border: '1px solid #E3ECF5' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E6F1FB'; e.currentTarget.style.borderColor = '#0077B6'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#CDE7F5'; e.currentTarget.style.borderColor = '#E3ECF5'; }}
                  >
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <p className="font-bold text-sm" style={{ color: '#03045E' }}>{b.name}</p>
                      {b.category && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold flex-shrink-0" style={{ backgroundColor: '#E6F1FB', color: '#0077B6' }}>{b.category}</span>
                      )}
                    </div>
                    {b.description && (
                      <p className="text-xs mb-2 line-clamp-2" style={{ color: '#6B7C93' }}>{b.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid #E3ECF5' }}>
                      <span className="text-xs" style={{ color: '#6B7C93' }}>📦 {count} item{count !== 1 ? 's' : ''}</span>
                      <span className="text-xs italic" style={{ color: '#0077B6' }}>Live pricing</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex justify-between items-center" style={{ borderTop: '1px solid #E3ECF5' }}>
          <button
            onClick={() => { onClose(); navigate('/templates'); }}
            className="hover:underline text-sm font-bold"
            style={{ color: '#0077B6' }}
          >
            + Manage Templates & Bundles
          </button>
          <button
            onClick={onClose}
            className="font-bold px-6 py-2 rounded-lg"
            style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default TemplatesPickerModal;
